'use client';

import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

/**
 * Scroll to `location.hash` after a client-side route change.
 *
 * Next.js scrolls to the hash when the link stays on the same page, but a
 * `Link` to `/en#faq` from another route landed at the top of the home page
 * (measured 2026-09-05: scrollY 0 with the FAQ section 7,700px down) because
 * the target did not exist yet when the router tried. The header is mounted
 * on every site page, so this hook runs once per route change and keeps
 * looking for the element for about a second.
 */
export function useHashScrollOnNavigation(): void {
  const pathname = usePathname();

  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;
    const id = decodeURIComponent(hash.slice(1));

    let tries = 0;
    let frame = 0;
    const attempt = () => {
      const el = document.getElementById(id);
      if (el) {
        // Honours the sections' scroll-margin-top for the fixed header, and
        // the visitor's reduced-motion setting like the rest of the header.
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
        el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        return;
      }
      if (tries++ < 60) frame = requestAnimationFrame(attempt);
    };
    frame = requestAnimationFrame(attempt);
    return () => cancelAnimationFrame(frame);
  }, [pathname]);
}
